/**
 * Card benefits that are **not** cashback (§9).
 *
 * The user drew this line, and it is the right one: cashback is money generated
 * *by spending*. A fuel surcharge waiver refunds a charge the bank itself
 * levied; a fee reversal undoes a fee. Both are real money back, so hiding them
 * would understate what a card is worth — but neither was earned, so counting
 * them would overstate what it *earns*.
 *
 * Hence a section that sits below the headline, styled deliberately quieter
 * than it: same information density, no brass. Brass on this page means "this
 * is Real Cashback", and that meaning is worth protecting.
 *
 * The fuel panel shows both sides on purpose. "₹95.36 waived" alone reads like
 * a gift; "₹95.36 waived of ₹101.03 levied — ₹5.67 net cost" is what actually
 * happened.
 */

import { Fuel, Receipt, TriangleAlert } from "lucide-react";

import { Panel } from "@/components/ui/primitives";
import type { Benefits as BenefitsData } from "@/lib/api/client";
import { formatMoney } from "@/lib/format";

function cardName(c: {
  nickname: string | null;
  product_name: string | null;
  issuer: string;
}) {
  return c.nickname || c.product_name || c.issuer;
}

export function Benefits({ data }: { data: BenefitsData }) {
  const { fuel } = data;
  const hasFuel = fuel.levied_count > 0 || fuel.waived_count > 0;
  const hasFees = data.fee_reversal_count > 0;

  if (!hasFuel && !hasFees) return null;

  return (
    <section className="space-y-4">
      <div>
        <h2 className="font-display text-base font-medium text-ink">
          Benefits · not cashback
        </h2>
        <p className="mt-1 max-w-prose text-sm text-muted">
          Real money back that you didn&rsquo;t earn by spending — so it&rsquo;s
          counted here rather than in the total above.{" "}
          <span className="text-faint">
            {formatMoney(data.total)} this year.
          </span>
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {hasFuel && <FuelPanel fuel={fuel} cards={data.fuel_cards} />}
        {hasFees && (
          <FeeReversalPanel
            total={data.fee_reversals}
            count={data.fee_reversal_count}
            cards={data.fee_reversal_cards}
          />
        )}
      </div>
    </section>
  );
}

function FuelPanel({
  fuel,
  cards,
}: {
  fuel: BenefitsData["fuel"];
  cards: BenefitsData["fuel_cards"];
}) {
  const levying = cards.filter((c) => c.levied_count > 0 || c.waived_count > 0);

  return (
    <Panel className="flex flex-col p-5">
      <div className="flex items-center gap-2">
        <Fuel className="h-4 w-4 text-muted" aria-hidden />
        <h3 className="text-sm font-medium text-ink">Fuel surcharge</h3>
      </div>

      <div className="mt-4 font-display text-figure font-semibold tabular-nums text-ink">
        {formatMoney(fuel.waived)}
      </div>
      <p className="mt-1 text-sm text-muted">
        waived of {formatMoney(fuel.levied)} levied
        {fuel.waived_share && (
          <>
            {" · "}
            <span className="text-ink tabular-nums">{fuel.waived_share}%</span>
          </>
        )}
      </p>

      <dl className="mt-4 space-y-2 border-t border-hairline pt-4 text-sm">
        <Row
          label="On fuel spend of"
          value={formatMoney(fuel.fuel_spend)}
          hint={`${fuel.fuel_txn_count} purchase${fuel.fuel_txn_count === 1 ? "" : "s"}`}
        />
        <Row
          label="Net cost to you"
          value={formatMoney(fuel.net_cost)}
          hint={
            Number(fuel.net_cost) > 0
              ? "the part not waived"
              : "waived in full or more"
          }
        />
        {fuel.observed_rate && (
          <Row
            label="Surcharge rate"
            value={`${fuel.observed_rate}%`}
            hint="charged, before waiver"
          />
        )}
      </dl>

      {levying.length > 1 && (
        <ul className="mt-4 space-y-1.5 border-t border-hairline pt-4">
          {levying.map((c) => (
            <li
              key={c.card_id}
              className="flex items-baseline justify-between gap-3 text-sm"
            >
              <span className="min-w-0 truncate text-muted" title={cardName(c)}>
                {cardName(c)}
              </span>
              <span className="shrink-0 tabular-nums text-ink">
                {formatMoney(c.waived)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* The denominator's own audit. A surcharge with no fuel purchase beside
          it means a merchant rule is missing an outlet — which would make the
          waiver look more generous than it was. */}
      {fuel.reconciled === false ? (
        <p className="mt-4 flex items-start gap-2 border-t border-hairline pt-3 text-xs text-warn">
          <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            {fuel.unmatched_surcharges} surcharge
            {fuel.unmatched_surcharges === 1 ? "" : "s"} (
            {formatMoney(fuel.unmatched_surcharge_amount)}) have no fuel purchase
            behind them, so the spend figure above is a floor. Add a merchant rule
            for that outlet in Settings.
          </span>
        </p>
      ) : (
        fuel.reconciled === true &&
        fuel.observed_rate && (
          <p className="mt-4 border-t border-hairline pt-3 text-xs text-faint">
            Checked: every surcharge line has a fuel purchase behind it, at a
            steady {fuel.observed_rate}% — so the spend figure is complete.
          </p>
        )
      )}
    </Panel>
  );
}

function FeeReversalPanel({
  total,
  count,
  cards,
}: {
  total: string;
  count: number;
  cards: BenefitsData["fee_reversal_cards"];
}) {
  return (
    <Panel className="flex flex-col p-5">
      <div className="flex items-center gap-2">
        <Receipt className="h-4 w-4 text-muted" aria-hidden />
        <h3 className="text-sm font-medium text-ink">Fee reversals</h3>
      </div>

      <div className="mt-4 font-display text-figure font-semibold tabular-nums text-ink">
        {formatMoney(total)}
      </div>
      <p className="mt-1 text-sm text-muted">
        {count} reversal{count === 1 ? "" : "s"} across {cards.length} card
        {cards.length === 1 ? "" : "s"}
      </p>

      <ul className="mt-4 space-y-3 border-t border-hairline pt-4">
        {cards.map((c) => (
          <li key={c.card_id}>
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="min-w-0 truncate text-ink" title={cardName(c)}>
                {cardName(c)}
              </span>
              <span className="shrink-0 font-medium tabular-nums text-ink">
                {formatMoney(c.amount)}
              </span>
            </div>
            {/* The bank's own wording. A fee reversal usually arrives as two
                lines — the fee and its GST — and showing both is how the total
                stops looking like a typo. */}
            <ul className="mt-1 space-y-0.5">
              {c.descriptions.map((d, i) => (
                <li
                  key={`${c.card_id}-${i}`}
                  className="truncate text-xs text-faint"
                  title={d}
                >
                  {d}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function Row({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted">
        {label}
        {hint && <span className="ml-1.5 text-xs text-faint">{hint}</span>}
      </dt>
      <dd className="shrink-0 tabular-nums text-ink">{value}</dd>
    </div>
  );
}
